<template>
  <div class="text-gray-700">
    <div class="flex items-center justify-between">
      <button
        class="mb-3 flex items-center gap-2 rounded-full border border-gray-300 px-2 py-1 text-xs text-gray-500 hover:border-gray-500 hover:text-gray-700 md:mb-5 md:text-sm"
        @click="goBack"
      >
        <ChevronLeftIcon class="size-3 md:size-4" />
        <span>Facturas por despachos</span>
      </button>
      <a
        :href="freeShippingInvoice.invoiceUrl"
        target="_blank"
        class="mb-3 flex items-center gap-2 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-500 hover:border-gray-500 hover:text-gray-700 hover:shadow-md md:mb-5 md:text-base"
      >
        <ArrowDownOnSquareIcon class="size-3 md:size-4" />
        <span>Descargar factura #{{ freeShippingInvoice.invoiceNumber }}</span>
      </a>
    </div>
    <div class="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:p-6">
      <h2 class="mb-4 text-lg font-semibold text-gray-900 md:text-xl">
        Factura de despacho #{{ freeShippingInvoice.invoiceNumber }}
      </h2>
      <div class="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
        <div class="rounded-md bg-gray-50 p-3 md:p-4">
          <dt class="text-xs font-medium text-gray-500 md:text-sm">Periodo</dt>
          <dd class="mt-1 text-base font-semibold text-gray-900 md:text-lg">
            {{ t(`date.month.${freeShippingInvoice.month}`) }} de {{ freeShippingInvoice.year }}
          </dd>
        </div>

        <div class="rounded-md bg-slate-50 p-3 md:p-4">
          <dt class="text-xs font-medium text-slate-600 md:text-sm">Monto total de la factura</dt>
          <dd class="mt-1 text-base font-semibold text-slate-900 md:text-lg">
            {{ useFormatCurrency(freeShippingInvoice.invoiceTotalAmountCents) }}
          </dd>
        </div>

        <div class="rounded-md bg-emerald-50 p-3 md:p-4">
          <dt class="text-xs font-medium text-emerald-600 md:text-sm">Monto neteado</dt>
          <dd class="mt-1 text-base font-semibold text-emerald-900 md:text-lg">
            {{ useFormatCurrency(nettedAmountCents) }}
          </dd>
        </div>
      </div>
    </div>
    <p
      v-if="makerPaymentRegistersIsLoading"
      class="text-sm md:text-base"
    >
      Cargando...
    </p>
    <div v-else>
      <h3 class="mb-4 text-lg font-semibold text-gray-900 md:text-xl">Registros de pago</h3>
      <div class="overflow-x-auto">
        <table class="min-w-full rounded-lg border border-gray-200 bg-white text-gray-500 shadow-sm">
          <thead class="bg-gray-50 text-center text-xs uppercase md:text-sm">
            <tr>
              <th class="border-b border-gray-200 px-6 py-3 font-medium tracking-wider">Monto</th>
              <th class="border-b border-gray-200 px-6 py-3 font-medium tracking-wider">Pedido</th>
              <th class="border-b border-gray-200 px-6 py-3 font-medium tracking-wider">Documento/s</th>
              <th class="border-b border-gray-200 px-6 py-3 font-medium tracking-wider">Estado de pago del pedido</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white text-xs md:text-sm">
            <tr
              v-for="makerPaymentRegister in makerPaymentRegisters"
              :key="`maker-payment-register-${makerPaymentRegister.id}`"
              class="text-center transition-colors duration-150 hover:bg-gray-50"
            >
              <td class="whitespace-nowrap px-6 py-4 font-medium">
                {{ useFormatCurrency(makerPaymentRegister.amountCents) }}
              </td>
              <td class="whitespace-nowrap px-6 py-4">
                <a
                  :href="`/sub_orders/${makerPaymentRegister.subOrderId}`"
                  target="_blank"
                  class="hover:underline"
                >
                  #{{ makerPaymentRegister.subOrderId }}</a
                >
              </td>
              <td class="whitespace-nowrap px-6 py-4">
                <ul class="flex flex-col gap-1">
                  <li
                    v-for="documentCodeNumber in makerPaymentRegister.documentCodeNumbers"
                    :key="`document-code-number-${documentCodeNumber}`"
                  >
                    {{ documentCodeNumber }}
                  </li>
                </ul>
              </td>
              <td class="whitespace-nowrap px-6 py-4">
                <span
                  class="rounded-full px-2 py-1 text-xs uppercase"
                  :class="
                    makerPaymentRegister.paymentStatus === 'paid'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  "
                >
                  {{ makerPaymentRegister.paymentStatus === 'paid' ? 'Pagado' : 'Pago parcial' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import { useQuery } from 'vue-query';
import { sumBy } from 'lodash';
import { ArrowDownOnSquareIcon, ChevronLeftIcon } from '@heroicons/vue/24/solid';
import useFormatCurrency from './use/format-currency';
import { useI18n } from 'vue-i18n';
import freeShippingInvoicesApi from 'api/free-shipping-invoices';
import useUrlMethods from './use/url-methods';

const props = defineProps<{
  freeShippingInvoice: FreeShippingInvoice;
}>();

const { t } = useI18n();

const makerPaymentRegistersQuery = useQuery(
  ['maker-payment-registers', props.freeShippingInvoice.id],
  () => freeShippingInvoicesApi.makerPaymentRegisters(props.freeShippingInvoice.id),
  {
    enabled: computed(() => !!props.freeShippingInvoice.id),
  },
);
const makerPaymentRegistersIsLoading = computed(() => makerPaymentRegistersQuery.isLoading.value);
const makerPaymentRegisters = computed(
  () => makerPaymentRegistersQuery.data.value?.data.makerPaymentRegisters as MakerPaymentRegister[],
);
const nettedAmountCents = computed(() => sumBy(makerPaymentRegisters.value, 'amountCents'));

const emit = defineEmits<{
  'go-back': [];
}>();

const { updateUrlParams } = useUrlMethods();
function goBack() {
  updateUrlParams({ shippingInvoice: '' });
  emit('go-back');
}
</script>
